'use client';
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useParams } from 'next/navigation';
import MuiContainer from '@new-components/atoms/MuiContainer';
import { ReactSlickCarouselType, SlickCarousel } from '../../atoms/SlickCarousel';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiBox from '@new-components/atoms/MuiBox';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import CommunityPropertyCard from '../CommunityPropertyCard';
import { MediaQuery } from '@constants/theme';
import MuiIconButton from '@new-components/atoms/MuiIconButton';
import MuiSkeleton from '@new-components/atoms/MuiSkeleton';
import {
    NextArrow,
    PrevArrow,
} from '@new-components/templates/HomeFeaturedCommunities/icons';
import { Desktop } from '@components/Desktop';
import { Mobile } from '@components/Mobile';
import MuiGrid from '@new-components/atoms/MuiGrid';
import { CommunityPropertyFilter } from '../CommunityPropertyFilter';
import useUnitsForSale from '@hooks/querys/useUnitsForSale';
import { Unit } from '@services/new/community/getUnitsForSale';
import { Route } from '@constants/appRoutes';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

const useCurrentBreakpoint = () => {
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    if (isLg) return 'lg';
    if (isMd) return 'md';
    return 'sm';
};
const PropertiesForSaleCarousel = styled(SlickCarousel)(({ theme }) => ({
    ':global .slick-slider': {
        overflow: 'hidden !important',
    },
    '& .slick-slide': {
        paddingRight: '16px',
        [MediaQuery.desktop]: {
            paddingRight: '24px',
        }
    },
}));
export interface PropertiesForSaleProps {
    communityName: string;
    title: string;
    subTitle?: string;
}
const PropertiesForSale: React.FC<PropertiesForSaleProps> = (props) => {
    const { isLoading, data } = useUnitsForSale(props.communityName);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);

    useEffect(() => {
        if (data?.units) {
            if (selectedOptions.length) {
                const filteredUnits = data.units.filter((unit) =>
                    selectedOptions.includes(unit.key)
                );
                setUnits(filteredUnits);
            } else {
                setUnits(data.units);
            }
        }
    }, [data, selectedOptions]);

    const sliderRef = React.useRef<ReactSlickCarouselType>(null);
    const [slideIndex, setSlideIndex] = React.useState(0);
    const breakPoint = useCurrentBreakpoint();
    const colCount = useMemo(() => {
        if (breakPoint === 'lg') {
            return 4;
        }
        return 3;
    }, [breakPoint]);
    const navDisableStatus = React.useMemo(() => {
        return {
            prev: slideIndex === 0,
            next: slideIndex >= units.length - colCount,
        };
    }, [slideIndex, colCount]);
    const allPropertiesLink = useMemo(() => {
        return Route.withQueryStr(
            Route.UNITS(),
            `text=${props.communityName}`
        )
    },[]);
    const router = useRouter();
    const { lang } = useParams();
    const handleNextClick = () => {
        if (sliderRef.current && sliderRef.current.slickNext) {
            sliderRef.current.slickNext();
        }
    };
    const handlePrevClick = () => {
        if (sliderRef.current && sliderRef.current.slickPrev) {
            sliderRef.current.slickPrev();
        }
    };
    const handleBeforeChange = (next: number) => {
        setSlideIndex(next);
    };
    const handleOptionClick = (key: string) => {
        if (selectedOptions.includes(key)) {
            const updatedSelectionOptions = selectedOptions.filter(
                (selectedOption) => selectedOption !== key
            );
            setSelectedOptions(updatedSelectionOptions);
        } else {
            setSelectedOptions([...selectedOptions, key]);
        }
    };
    const ContentLoader = () => {
        return (
            <MuiBox>
                <MuiBox sx={{ width: '100%', height: '320px' }}>
                    <MuiSkeleton sx={{ width: '100%', height: '100%' }} />
                </MuiBox>
            </MuiBox>
        );
    };

    return (
        <MuiContainer
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                px: units.length === 1 ? '24px' : 0,
                pb: '32px',
                [MediaQuery.desktop]: {
                    py: '96px',
                },
            }}>
            <MuiBox
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: units.length === 1 ? 0 : '24px',
                    [MediaQuery.desktop]: {
                        px: '0',
                    },
                }}>
                <MuiBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <MuiTypography
                        sx={{
                            fontWeight: { xs: 400, md: 300 },
                            fontSize: { xs: '24px', md: '48px' },
                            lineHeight: { xs: '30px', md: '60px' },
                            letterSpacing: { xs: '-0.4px', md: '-0.8px' },
                            color: '#003243',
                            textAlign: 'left',
                            marginBottom: { xs: "12px", md: "24px" },
                        }}>
                        {props.title}
                    </MuiTypography>
                    {props.subTitle && (
                        <MuiTypography
                            sx={{
                                color: '#003243',
                                fontSize: { xs: '14px', md: '17px' },
                                fontWeight: 400,
                                lineHeight: '125%',
                                letterSpacing: '-0.4px',
                                textAlign: 'left',
                                opacity: .6,
                            }}>
                            {props.subTitle}
                        </MuiTypography>
                    )}
                </MuiBox>


            </MuiBox>
            {!isLoading && units.length === 0 && (
                <MuiContainer sx={{ display: "flex", justifyContent: 'center', alignItems: "center", flexDirection: "column", py: "32px", gap: "12px" }}>
                    <ErrorOutlineOutlinedIcon fontSize='large' sx={{opacity:.5}}/>
                    <MuiTypography
                        sx={{
                            color: '#2d2d2dcc',
                            maxWidth: '800px',
                            opacity: '.5'
                        }}
                        variant='body1'>
                        No properties were available to list
                    </MuiTypography>
                </MuiContainer>
            )}
            {units.length > 3 && (
                <MuiBox
                    sx={{
                        display: 'none',
                        gap: 2,
                        [MediaQuery.desktop]: { display: 'flex', alignItems: 'flex-end' },
                    }}>
                    <MuiIconButton
                        disabled={navDisableStatus.prev}
                        onClick={handlePrevClick}
                        data-testid='fcc-prev-button'
                        sx={{
                            'width': '24px',
                            'height': '24px',
                            '&.Mui-disabled': {
                                opacity: 0.5,
                            },
                        }}>
                        <PrevArrow />
                    </MuiIconButton>
                    <MuiIconButton
                        disabled={navDisableStatus.next}
                        onClick={handleNextClick}
                        data-testid='fcc-next-button'
                        sx={{
                            'width': '24px',
                            'height': '24px',
                            '&.Mui-disabled': {
                                opacity: 0.5,
                            },
                        }}>
                        <NextArrow />
                    </MuiIconButton>
                </MuiBox>
            )}
            {isLoading ? (
                <ContentLoader />
            ) : units.length > 0 ? (
                <>
                    <MuiContainer sx={{ margin: { md: "0" }, padding: { md: "0" } }}>
                        <CommunityPropertyFilter
                            selectedOptions={selectedOptions}
                            options={data?.options!}
                            handleOptionClick={handleOptionClick}
                        />
                    </MuiContainer>
                    <MuiBox sx={{ overflow: 'hidden', width: '100%' }}>
                        <Mobile>
                            {units.length === 1 ? (
                                <CommunityPropertyCard
                                    communityName={props.communityName}
                                    {...units[0]}
                                />
                            ) : (
                                <PropertiesForSaleCarousel
                                    ref={sliderRef}
                                    slidesToShow={1}
                                    centerMode
                                    centerPadding={'16px'}
                                    beforeChange={(_: number, next: number) =>
                                        handleBeforeChange(next)
                                    }
                                    swipeToSlide>
                                    {units.map((item: Unit, index: number) => (
                                        <CommunityPropertyCard
                                            communityName={props.communityName}
                                            {...item}
                                        />
                                    ))}
                                </PropertiesForSaleCarousel>
                            )}
                        </Mobile>
                        <Desktop>
                            {units.length <= colCount ? (
                                <MuiGrid container spacing={4} gap='24px'>
                                    {units.map((item: Unit, index: number) => (
                                        <MuiGrid item key={index} md={4} lg={3}>
                                            <CommunityPropertyCard
                                                communityName={props.communityName}
                                                {...item}
                                            />
                                        </MuiGrid>
                                    ))}
                                </MuiGrid>
                            ) : (
                                <PropertiesForSaleCarousel
                                    ref={sliderRef}
                                    slidesToShow={breakPoint === 'md' ? 3 : 4}
                                    beforeChange={(_: number, next: number) =>
                                        handleBeforeChange(next)
                                    }
                                    infinite={false}>
                                    {units.map((item: Unit, index: number) => (
                                        <CommunityPropertyCard
                                            communityName={props.communityName}
                                            {...item}
                                            key={item.key}
                                        />
                                    ))}
                                </PropertiesForSaleCarousel>
                            )}
                        </Desktop>
                    </MuiBox>
                    {<MuiContainer sx={{ margin: { md: "0" }, padding: { md: "0" } }}>
                        {allPropertiesLink && <Link
                            href={allPropertiesLink}
                            prefetch={false}
                            style={{
                                textDecoration: 'none',
                                border: '1px solid #BABAC8',
                                padding: '0px 32px',
                                height: '52px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                borderRadius: '500px',
                            }}>
                            <MuiTypography
                                sx={{
                                    color: '#2d2d2d',
                                    fontSize: '17px',
                                    fontWeight: 400,
                                    lineHeight: '140%',
                                    letterSpacing: '-0.4px',
                                    textTransform: 'capitalize',
                                }}>
                                Explore All Properties
                            </MuiTypography>
                        </Link>}
                    </MuiContainer>}
                </>
            ) : null}
        </MuiContainer>
    );
};
export default PropertiesForSale;
